// Real-time feedback #1 (Part B): optimistic in-payload mutation for
// lifecycle/priority feedback kinds.
//
// Mirror of app.ts::applyKilEventToPayload, extracted into its own
// module so it's unit-testable (app.ts has top-level bootstrap side
// effects that run on import). Used in two places by renderRun:
//   1. in-session, on a `feedback-state-changed` CustomEvent, and
//   2. at load, replaying the recipient's fetched events onto the
//      payload (closes/priority don't touch the published blob until
//      the next cron, so this is what makes the dim/move survive a
//      reload / navigation).
//
// Each branch mirrors the agent absorber (agent/feedback_absorb.py) so
// the optimistic state matches what the next cron will render — a
// divergence here would "revert" after absorb.

import { actionItemSlug } from "./target_id";

/** The subset of the v4 briefing payload this applier reads/writes.
 *  Structurally compatible with the renderer's payload shape. */
export interface OptimisticPayload {
  threads?: Array<{
    slug?: string;
    // New-Category flow (Ray 2026-09-22): category_add pushes a stub Category
    // node whose title renders from `name` (renderCategoryV4 reads `thread.name`,
    // and the empty-category fold toggle reads it too). Present on every real
    // payload thread node; the type only omitted it because no optimistic kind
    // needed it before (the R19-deferred field).
    name?: string;
    status?: string;
    // #37: phase band. category_move_to_phase mutates it in-session so the
    // render re-bands the Category before the next cron stamps it from state.
    phase?: string | null;
    // P4d-3: sub-project band. category_move_to_subproject mutates it in-session
    // so the render re-bands the Category before the next cron re-projects it.
    sub_project_slug?: string | null;
    // #38: sub-category grouping overlay. Pending add/rename/close/reopen
    // events mutate this list in-session so pickers + the render partition
    // see groups before the next cron stamps them from state.
    subcategories?: Array<{
      slug?: string;
      name?: string;
      status?: string;
      created_at?: string | null;
      closed_at?: string | null;
      closed_reason?: string | null;
      seq?: number | null;
    }>;
    work_items?: Array<{
      slug?: string;
      // OPT-1a: action_to_item pushes a stub Item whose title renders from `name`
      // (renderItemV4 reads wi.name). Present on every real payload node.
      name?: string;
      status?: string;
      // #38: grouping pointer (null = ungrouped). item_move_to_subcategory
      // sets it; a reopen onto a closed group NULLs it (policy 3).
      subcategory_slug?: string | null;
      closed_reason?: string | null;
      closure_candidate?: unknown;
      // A2 (Ray 2026-09-13): item_set_report_tags echoes the Item-tier report
      // gutter chip in-session (the row render reads this field).
      report_tags?: string[] | null;
      // E-7c (Ray 2026-09-15): work_item_set_narrative echoes the edited Item
      // narrative in-session (the render reads this field). Without it a
      // narrative edit was a no-echo the next renderViewer wiped — "some changes
      // take effect, others don't."
      narrative?: string | null;
      // KIL-POINT (Ray 2026-10-28): item_set_point echoes the Item's 558 "Point"
      // in-session (renderItemV4 reads this for its quiet "Point:" meta bit).
      // Without it a set-Point was a no-echo the next renderViewer wiped.
      point?: string | null;
      action_items?: Array<{
        ai_slug?: string;
        status?: string;
        // N1 (Ray 2026-09-22): action_rename echoes the Action title in-session
        // (renderActionV4 reads `text`). Present on every real payload node.
        text?: string;
        closed_reason?: string | null;
        closure_candidate?: unknown;
        priority_status?: string;
        reason?: string | null;
        impact?: string | null;
        off_hold_condition?: string | null;
        // A2 (Ray 2026-09-13): action_set_report_tags echoes the KIL/558 gutter
        // chip in-session. (The PSU chip already echoes via the separate
        // `kil-mark-changed` → applyKilEventToPayload path; report_tags had no
        // optimistic path, so a just-set 558 tag stayed invisible until the next
        // cron — "I set both, only PSU showed".)
        report_tags?: string[] | null;
        // A1 (Ray 2026-09-15): action_set_associations echoes the row's `.atag`
        // chip in-session; the Item chip rolls these up (itemAssociationUnion).
        association_tags?: string[] | null;
        // E-7c (Ray 2026-09-15): action_set_narrative echoes the edited Action
        // narrative in-session (the render reads this field).
        narrative?: string | null;
        // Client 2026-08-12: the rendered question list. A dismissal drops its
        // question from here on submit so the row disappears immediately —
        // otherwise the recipient clicks "Dismiss question", nothing visibly
        // happens, and the clutter he asked us to remove is still on screen
        // until the ~10-minute mid-week refresh.
        // A1 (Ray 2026-09-13): a resolve writes `answer`/`answered_at` here so an
        // accepted answer survives the next re-render (it was a DOM-only echo →
        // reverted on the next optimistic action's renderViewer).
        open_questions_v4?: Array<{
          qid?: string;
          text?: string;
          answer?: string | null;
          answered_at?: string | null;
        }>;
      }>;
    }>;
  }>;
  // R19 (Ray 2026-09-22): the Needs-Your-Input queues. A placement echoes by
  // removing the placed entry from here (so it clears from Needs Your Input) and
  // materializing the placed node under its thread. Only `id` is read/written.
  unfiled?: Array<{ id?: string }>;
  informational?: Array<{ id?: string }>;
  // S4 (Ray 2026-10-27): the project-level executive summary (singleton) +
  // the per-sub-project summary map. set_executive_summary echoes by writing
  // the edited text here so the rendered summary updates in-session instead of
  // only on the next cron (it's a blob-level field — the edit ingests but had
  // no optimistic path; #395 visibility-gap, one tier up). briefings.ts renders
  // the band summary from sub_project_summaries[slug] at render time.
  executive_summary?: string;
  sub_project_summaries?: Record<string, string>;
}

/** The lifecycle/priority kinds this applier handles. forms.ts gates the
 *  `feedback-state-changed` dispatch on the same set. */
export const OPTIMISTIC_STATE_KINDS: readonly string[] = [
  "action_close",
  "action_reopen",
  "work_item_close",
  "work_item_reopen",
  // C5 (Ray 2026-10-18, was 2026-07-21): the disposition verbs echo in-session —
  // an Action/Item acknowledged or marked not-relevant is REMOVED immediately
  // (2026-10-18: was a gray pill that lingered until the cron; the cron drops it
  // either way) AND the parent's close gate (openActionCount) recomputes — un-graying
  // "Close Item" once no OPEN Action remains. Previously only close/reopen echoed, so
  // acknowledging an Action's siblings left the Item stuck un-closable in-session.
  "work_item_acknowledge",
  "action_acknowledge",
  "work_item_not_relevant",
  "action_not_relevant",
  // C5 (Ray 2026-10-18): informational_acknowledge now echoes in-session — the
  // acknowledged Needs-Input row drops from the active queue immediately (it was
  // the one disposition verb with no optimistic path; the generic submit gates the
  // feedback-state-changed dispatch on this set, forms.ts:7286).
  "informational_acknowledge",
  "thread_close",
  "thread_reopen",
  "action_dismiss_closure_candidate",
  // Option Y (2026-06-03): dismiss an Item-level closure proposal — the
  // "(Agent proposes closing)" suffix clears optimistically.
  "work_item_dismiss_closure_candidate",
  "action_priority_change",
  // A①: a merge CLOSES its sources (the absorber does the same), so they
  // gray into Recently Closed on submit — fixes Ray's "by the 4th merge
  // I'm lost, nothing happened so I can't tell which ones I folded."
  "work_item_merge",
  "action_merge",
  // Reorg-toast fan-out (Ray 2026-09-16): a Category merge echoes in-session so
  // its "Merged into X" toast isn't a lie — the source Category's Items move to
  // the target and the emptied source grays. Mirrors apply_thread_merge's
  // user-visible effect (the agent does the precise synonym/sub-category
  // migration on absorb).
  "thread_merge",
  // New-Category flow (Ray 2026-09-22): a manually-added Category echoes
  // in-session by materializing a stub thread node so it shows pre-cron AND the
  // move/place pickers (which read payload.threads) can target it. Was NOT
  // optimistic — Ray created "Construction Progress" and couldn't find it until
  // the weekly cron (mirror of item_add's visibility gap, one tier up). The
  // stub lands ungrouped under (No sub-project)/General — where the absorber
  // (apply_v4_category_add → upsert_thread, null sub_project/phase) also puts
  // it — so the echo faithfully previews the cron landing (renders in the
  // empty-category fold). The recipient's retained category_add event replays
  // this on load; idempotent by slug once the cron materializes the thread.
  "category_add",
  // #38: sub-category grouping echoes in-session so the recipient sees the
  // regroup pre-cron AND the move picker / "≥1 open group" gate see pending
  // adds without a reload (the pending-aware picker mechanism).
  "subcategory_add",
  "subcategory_rename",
  "subcategory_close",
  "subcategory_reopen",
  // #74b: delete echoes in-session — the group vanishes + its Items re-render
  // un-grouped without waiting for the next cron.
  "subcategory_delete",
  "item_move_to_subcategory",
  // P-5 fan-out ② (Ray 2026-09-16): re-parenting an Item to a different Category
  // echoes in-session — the Item splices out of the source Category and appends
  // to the target (mirror of apply_v4_item_move_to_category), exiting its old
  // sub-category group. The Item-tier sibling of action_move_to_item; completes
  // the real-time Reorganize surface ("that whole reorganization section").
  // Numbers stay stale in-session (the agent re-numbers under the target).
  "item_move_to_category",
  // #37: re-banding echoes in-session — the Category jumps to its new phase
  // band on submit without waiting for the next cron's stamp.
  "category_move_to_phase",
  // P4d-3: filing a Category under a sub-project echoes in-session — it jumps to
  // the new sub-project band on submit. Numbers stay stale in-session (the
  // re-root is agent-side); the next re-render/cron re-letters them.
  "category_move_to_subproject",
  // #76: promote-to-Item echoes by closing the source Action in-session (the
  // new Item materializes on the next cron).
  "action_to_item",
  // Visibility-gap (Ray 2026-09-22): a manually-added Item echoes in-session by
  // materializing a stub node under its target Category. Was NOT optimistic AND
  // the mid-week rerender never materializes a new work_item, so an added Item
  // stayed invisible until the weekly cron and could not be moved (pickers read
  // the payload) — Ray's "I can't see the items I created". The recipient's
  // retained item_add events replay this on load, so an already-absorbed add
  // reappears too. Mirrors action_to_item's item push.
  "item_add",
  // F5 (Ray 2026-09-24): a manually-added Action echoes in-session by
  // materializing a stub Action under its parent Item. Was NOT optimistic — an
  // added Action stayed invisible until the weekly cron, INCLUDING when the
  // parent Item is itself a same-session stub (a just-promoted action_to_item /
  // just-added item_add). Sibling of item_add's visibility gap, one tier down.
  // The recipient's retained action_add events replay this on load; idempotent
  // by ai_slug so the cron materialization is a no-op (no double).
  "action_add",
  // P-5 (Ray 2026-09-16): re-parenting an Action to a different Item echoes
  // in-session — the Action splices out of the source Item and appends to the
  // target (mirror of apply_v4_action_move_to_item). Without this the move was
  // a no-echo the next renderViewer wiped AND the source Item's in-session
  // action_items still listed the moved Action, so a subsequent
  // work_item_close_cascade on the source (briefings.ts openChildActions) closed
  // it — Ray's "I moved two actions then closing the old item closed them too"
  // (C9.1.5/6 → C7.4, both wrongly closed). The splice removes it from the
  // cascade's collection. Starts the real-time reorg (build order ②).
  "action_move_to_item",
  // Client 2026-08-12: dismissing a question hides it in-session. The whole
  // point of the affordance is removing visual clutter, so a dismissal that
  // leaves the question on screen has done nothing the recipient can see.
  "action_open_question_dismiss",
  // A1 (Ray 2026-09-13): accepting/answering a question echoes in-session — the
  // sibling of dismiss above. Without it the ✓ answer was a DOM-only echo that
  // the next optimistic action's renderViewer wiped ("I accept it, then answering
  // the next question resets the prior"). The applier writes answer/answered_at
  // onto the matching qid; the accept path (forms.ts:acceptProposedAnswer) also
  // dispatches feedback-state-changed so its one-click path re-renders too.
  "action_open_question_resolve",
  // A2 (Ray 2026-09-13): setting the Key Issue Log (558) report tag echoes the
  // briefing-row gutter chip in-session. The PSU chip already echoes via the
  // separate `kil-mark-changed` path; report_tags had none, so "I set both,
  // only PSU showed". Item tier mirrors it for the item-level gutter chip.
  "action_set_report_tags",
  "item_set_report_tags",
  // B (Ray 2026-09-16, PSU→item-only): the Item's OWN PSU mark echoes in-session
  // — the item-header PSU badge flips on submit via the app.ts applier (which sets
  // wi.kil_marked). The Item pencil's "Project status update" checkbox posts one
  // item_kil_mark/unmark to the Item (forms.ts collectItemReportCascade); the
  // action tier no longer carries PSU. Was "inert back-compat" (#58 removed the
  // item PSU affordance); B revives it as the sole PSU membership signal.
  "item_kil_mark",
  "item_kil_unmark",
  // A1 (Ray 2026-09-15): setting an association echoes the row's `.atag` chip
  // in-session. Without it a set-association was a DOM-only/no echo the next
  // renderViewer wiped ("it doesn't show me the assignment… did I click it?").
  // Only the ACTION tier is optimistic: the Item pencil now CASCADES to its open
  // Actions (forms.ts collectItemAssociationCascade → per-Action
  // action_set_associations), and the Item's chip is a pure rollup of its
  // Actions (itemAssociationUnion), so the cascade's per-Action echoes recompute
  // the Item chip for free — no separate item_set_associations optimistic path.
  "action_set_associations",
  // E-7c (Ray 2026-09-15): a narrative edit echoes in-session — the edited Item/
  // Action narrative shows immediately instead of only on the next cron. Was a
  // no-echo the next renderViewer wiped ("some changes take effect, others
  // don't"). The generic submit (forms.ts) dispatches feedback-state-changed for
  // any kind in this set; the appliers above write `narrative`. No worker/schema
  // (both kinds already deployed).
  "work_item_set_narrative",
  "action_set_narrative",
  // KIL-POINT (Ray 2026-10-28): a Point edit echoes in-session — the Item's quiet
  // "Point:" meta bit updates immediately instead of only on the next cron. The
  // generic submit dispatches feedback-state-changed for any kind in this set; the
  // applier below writes `wi.point`. Already-deployed wire kind — no worker/schema.
  "item_set_point",
  // S4 (Ray 2026-10-27): an executive-summary edit echoes in-session — the
  // rendered summary <p> (project-level) or sub-project band summary updates
  // immediately instead of only on the next cron. Was a no-echo the next
  // renderViewer wiped, so Ray's edit "ingested but didn't show" on Quayside +
  // Peloro (systematic). The generic submit dispatches feedback-state-changed for
  // any kind in this set; the applier below writes payload.executive_summary (or
  // sub_project_summaries[slug] for a band edit). Already-deployed wire kind — no
  // worker/schema; the edit is reasserted onto the node every cron.
  "set_executive_summary",
  // N1 (Ray 2026-09-22): a rename echoes in-session. Was a no-echo — Ray renamed
  // an Item then moved it, and the following optimistic move re-rendered from the
  // base blob so the new name vanished ("I renamed it and moved it; the name
  // change was lost", Portofino A8.1). The rename correction is durable and
  // re-asserted post-upsert, so this is a visual echo only (mirrors E-7c). Both
  // kinds already deployed — no worker/schema.
  "work_item_rename",
  "action_rename",
  // R19 (Ray 2026-09-22): a Needs-Your-Input placement echoes in-session — the
  // placed entry clears from the queue AND the placed node materializes under its
  // thread. Was a no-echo + the mid-week rerender never materializes a new item,
  // so Ray's "I placed an item but it does not process the change" (the placement
  // absorbed into state but stayed invisible until the weekly cron). Mirrors
  // item_add's stub push. merge_into_existing echoes only the queue removal (its
  // fold into the existing entity settles on the cron). All existing wire kinds.
  "place_from_unfiled",
  "place_as_action",
  "place_as_category",
  "merge_into_existing",
];

/** Coerce a merge payload's source-slug field, which may be a JSON array or
 *  a single string (the absorb-gap shape), into a string[]. */
function toSlugList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((s) => String(s)).filter(Boolean);
  if (typeof v === "string" && v.trim()) return [v.trim()];
  return [];
}

/** R19: coerce a payload field to a trimmed string ("" when absent/blank). */
function optStr(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/** R19: a deterministic optimistic slug for a first-Action stub (the agent
 *  re-derives the real slug on the cron; this only needs stable in-session
 *  identity so a replayed placement doesn't double the row). */
function optSlug(text: string): string {
  // actionItemSlug mirrors the agent's 60-char cap: the echo slug MUST match what
  // the absorber stores, or a same-session follow-up correction on the echoed Action
  // dead-letters until the next cron re-renders it (SLUG-CAP-MISMATCH, 2026-10-06 —
  // the north-tower place_from_unfiled + set-association). Both call sites (:438,
  // :477) are Action slugs.
  return actionItemSlug(text) || "placed-action";
}

/** R19 (Ray 2026-09-22): remove a placed Needs-Your-Input entry (by its id) from
 *  the unfiled + informational queues so it clears from the SPA on submit/replay.
 *  Idempotent — a no-op once the entry is gone (the cron also removes it). */
function removeNeedsInputEntry(payload: OptimisticPayload, entryId: string): boolean {
  if (!entryId) return false;
  let removed = false;
  for (const key of ["unfiled", "informational"] as const) {
    const list = payload[key];
    if (!Array.isArray(list)) continue;
    const next = list.filter((e) => e?.id !== entryId);
    if (next.length !== list.length) {
      payload[key] = next;
      removed = true;
    }
  }
  return removed;
}

/** #38: today's date as ISO `YYYY-MM-DD` — the optimistic stand-in for the
 *  agent stamp's created_at / closed_at. State writes the real recorded date
 *  at absorb and the next cron re-projects it, so this only governs the
 *  pre-cron in-session render. */
function todayISODate(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Apply one feedback event to the in-memory payload. Returns true if it
 *  mutated something (parse miss / unknown kind / target not found → false). */
export function applyFeedbackEventToPayload(
  payload: OptimisticPayload,
  kind: string,
  targetId: string,
  eventPayload: Record<string, unknown>,
): boolean {
  const threadMatch = targetId.match(/^thread:([^/]+)$/);
  const itemMatch = targetId.match(/^thread:([^/]+)\/work_item:([^/]+)$/);
  const actionMatch = targetId.match(
    /^thread:([^/]+)\/work_item:([^/]+)\/action_item:([^/]+)$/,
  );
  // #38: sub-category targets — `thread:<t>/subcategory:<sc>`.
  const subcategoryMatch = targetId.match(
    /^thread:([^/]+)\/subcategory:([^/]+)$/,
  );

  // S4 (Ray 2026-10-27): executive-summary edit. The targetId is
  // `project:<slug>` (not thread/item/action), so it matches none of the
  // regexes above — it's keyed by the payload's `sub_project_slug` scope, the
  // same scope axis isSummaryEditForScope filters the submitted chips on
  // (briefings.ts:812). scope null = the project-level singleton; a non-empty
  // slug = that band's summary. The two agree on every value the form emits
  // (undefined → project, a real slug → band); they only differ on an empty
  // slug, which falls to the project-level write here (never a "" map key)
  // while isSummaryEditForScope would read it as band "" — unreachable, the
  // form never emits "". Writes the edited text so the rendered summary updates
  // in-session; the cron reasserts the pinned summary onto the node. A
  // recipient-typed `$` rides through verbatim (their own text — consistent
  // with the exec-summary $ grandfather).
  if (kind === "set_executive_summary") {
    const text = typeof eventPayload.summary === "string"
      ? eventPayload.summary : "";
    const scope = typeof eventPayload.sub_project_slug === "string"
      && eventPayload.sub_project_slug
      ? eventPayload.sub_project_slug : null;
    if (scope === null) {
      payload.executive_summary = text;
    } else {
      payload.sub_project_summaries = payload.sub_project_summaries ?? {};
      payload.sub_project_summaries[scope] = text;
    }
    return true;
  }

  // Category (thread) close/reopen. NB: KIL never parses thread-level
  // targets, so this match is unique to this applier.
  if (kind === "thread_close" || kind === "thread_reopen") {
    if (!threadMatch) return false;
    const [, threadSlug] = threadMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      t.status = kind === "thread_close" ? "closed" : "open";
      return true;
    }
    return false;
  }

  // New-Category flow (Ray 2026-09-22): category_add targets the PROJECT
  // (`project:<slug>`), not a thread — the new Category's own slug + name ride
  // in the payload (forms.ts slugifies `thread_slug` from `name` on submit).
  // Push a stub Category node so it shows in-session (in the empty-category fold
  // under (No sub-project)/General, where the cron will also land it) and the
  // move/place pickers can target it. Idempotent by slug — once the weekly cron
  // materializes the thread into the blob, the retained event's replay no-ops.
  // Mirror of item_add's stub push, one tier up (Category vs Item).
  if (kind === "category_add") {
    const slug = typeof eventPayload.thread_slug === "string"
      ? eventPayload.thread_slug : "";
    if (!slug) return false;
    const name = typeof eventPayload.name === "string" && eventPayload.name.trim()
      ? eventPayload.name.trim() : slug;
    const threads = (payload.threads ??= []);
    if (threads.some((t) => t.slug === slug)) return false;
    // N4b: echo the recipient's chosen placement so the stub lands in the band
    // the cron will put it in. Absent (legacy event / "(No sub-project)" →
    // dropped key) → null, the "(No sub-project)/General" default. Mirrors
    // apply_v4_category_add → upsert_thread(sub_project_slug, phase).
    const subProject = typeof eventPayload.sub_project_slug === "string"
      && eventPayload.sub_project_slug ? eventPayload.sub_project_slug : null;
    const phase = typeof eventPayload.phase === "string" && eventPayload.phase
      ? eventPayload.phase : null;
    threads.push({
      slug,
      name,
      status: "open",
      sub_project_slug: subProject,
      phase,
      work_items: [],
      subcategories: [],
    });
    return true;
  }

  // Visibility-gap (Ray 2026-09-22): item_add targets the Category (`thread:<t>`);
  // materialize a stub Item node under it so a manually-added Item shows in-session
  // and reappears on load (the recipient's retained item_add events replay here).
  // Mirror of action_to_item's item push (the shape the renderer + move/place
  // pickers expect). Idempotent by slug — when the weekly cron materializes the
  // Item into the blob, the guard makes this replay a no-op (no double). The stub
  // lands ungrouped under the Category (subcategory_slug null), matching where the
  // absorber (apply_v4_item_add) places it; Ray then moves it into a sub-category
  // via item_move_to_subcategory (already optimistic).
  if (kind === "item_add") {
    if (!threadMatch) return false;
    const [, threadSlug] = threadMatch;
    const slug = typeof eventPayload.work_item_slug === "string"
      ? eventPayload.work_item_slug : "";
    if (!slug) return false;
    const name = typeof eventPayload.name === "string" && eventPayload.name.trim()
      ? eventPayload.name.trim() : slug;
    const narrative = typeof eventPayload.narrative === "string"
      ? eventPayload.narrative : null;
    const targetThread = (payload.threads ?? []).find((t) => t.slug === threadSlug);
    if (!targetThread) return false;
    targetThread.work_items = targetThread.work_items ?? [];
    if (targetThread.work_items.some((wi) => wi.slug === slug)) return false;
    targetThread.work_items.push({
      slug,
      name,
      narrative,
      status: "open",
      subcategory_slug: null,
      action_items: [],
    });
    return true;
  }

  // F5 (Ray 2026-09-24): action_add targets the Item (thread:<t>/work_item:<wi>);
  // materialize a stub Action under it so a manually-added Action shows in-session.
  // It had NO optimistic path — the added Action stayed invisible until the cron,
  // including when the parent Item is itself a same-session stub (just promoted via
  // action_to_item, or just added via item_add — those stubs are already in the
  // optimistic layer). Find the parent by wi slug cross-thread (slugs are
  // project-unique) so a stub parent under (No sub-project) still matches. Mirrors
  // place_as_action's push. Idempotent by ai_slug → the cron materialization
  // replays as a no-op (no double); the recipient's retained action_add replays here.
  if (kind === "action_add") {
    if (!itemMatch) return false;
    const [, , wiSlug] = itemMatch;
    const aiSlug = optStr(eventPayload.action_item_slug) || optSlug(optStr(eventPayload.text));
    const text = optStr(eventPayload.text);
    if (!wiSlug || !aiSlug || !text) return false;
    const wi = (payload.threads ?? [])
      .flatMap((t) => t.work_items ?? [])
      .find((w) => w.slug === wiSlug);
    if (!wi) return false;
    wi.action_items = wi.action_items ?? [];
    if (wi.action_items.some((a) => a.ai_slug === aiSlug)) return false;
    wi.action_items.push({
      ai_slug: aiSlug,
      text,
      status: "open",
      priority_status: optStr(eventPayload.priority_status) || "Active",
      // C1 (Ray 2026-10-18): carry the narrative into the echoed node. Without it
      // a just-added Action rendered BLANK until the cron re-materialized it (the
      // narrative is never lost — the action_add correction carries it, and the
      // absorber passes narrative=; this closes the in-session display gap). The
      // ActionItem type already declares `narrative?`.
      narrative: optStr(eventPayload.narrative) || null,
    });
    return true;
  }

  // R19 (Ray 2026-09-22): place an unfiled row as a new Item — clear it from the
  // Needs-Your-Input queue AND materialize the new Item under new_item_thread_slug
  // (mirror item_add; the mid-week rerender never materializes a new item). Always
  // "Place as Item" (forms.ts strips target_work_item_slug). `complete` = born-closed.
  if (kind === "place_from_unfiled") {
    let changed = removeNeedsInputEntry(payload, optStr(eventPayload.unfiled_id));
    const toThread = optStr(eventPayload.new_item_thread_slug);
    const slug = optStr(eventPayload.new_item_work_item_slug);
    const t = (payload.threads ?? []).find((x) => x.slug === toThread);
    if (t && slug && !(t.work_items ?? []).some((wi) => wi.slug === slug)) {
      const status = eventPayload.complete === true ? "closed" : "open";
      const actionText = optStr(eventPayload.new_item_action_text);
      t.work_items = t.work_items ?? [];
      t.work_items.push({
        slug,
        name: optStr(eventPayload.new_item_name) || slug,
        narrative: typeof eventPayload.new_item_narrative === "string"
          ? eventPayload.new_item_narrative : null,
        status,
        subcategory_slug: optStr(eventPayload.new_item_subcategory_slug) || null,
        action_items: actionText ? [{
          ai_slug: optSlug(actionText),
          text: actionText,
          status,
          priority_status: optStr(eventPayload.new_item_action_priority) || "Active",
          // C1/C2 (Ray 2026-10-18): carry the first Action's narrative into the
          // echoed node (the Place-as-Item form now offers a name + narrative pair,
          // not one field that doubled as both). Blank until the cron before this.
          // The absorber persists it via apply_v4_action_add(narrative=…).
          narrative: optStr(eventPayload.new_item_action_narrative) || null,
        }] : [],
      });
      changed = true;
    }
    return changed;
  }

  // R19: place an unfiled row as a new Action under an existing Item — clear the
  // entry + push the Action under target_work_item_slug (found cross-thread by slug).
  if (kind === "place_as_action") {
    let changed = removeNeedsInputEntry(payload, optStr(eventPayload.entry_id));
    const wiSlug = optStr(eventPayload.target_work_item_slug);
    const aiSlug = optStr(eventPayload.action_item_slug);
    const text = optStr(eventPayload.text);
    if (wiSlug && aiSlug && text) {
      const wi = (payload.threads ?? [])
        .flatMap((t) => t.work_items ?? [])
        .find((w) => w.slug === wiSlug);
      if (wi) {
        wi.action_items = wi.action_items ?? [];
        if (!wi.action_items.some((a) => a.ai_slug === aiSlug)) {
          wi.action_items.push({
            ai_slug: aiSlug,
            text,
            status: eventPayload.complete === true ? "closed" : "open",
            priority_status: optStr(eventPayload.priority_status) || "Active",
            // Place-as-Action narrative (Ray 2026-10-21): echo it immediately so a
            // placed Action doesn't render blank until the cron (C1-style carry; the
            // absorber persists it via apply_v4_action_add(narrative=…)).
            narrative: optStr(eventPayload.narrative) || null,
          });
          changed = true;
        }
      }
    }
    return changed;
  }

  // R19: place_as_category (new Category) and merge_into_existing (fold into an
  // existing entity) echo only the queue removal in-session — the new Category /
  // the fold settles on the cron; clearing the entry is the "it processed" signal.
  if (kind === "place_as_category" || kind === "merge_into_existing") {
    return removeNeedsInputEntry(payload, optStr(eventPayload.entry_id));
  }

  // #37: category_move_to_phase targets the Category (`thread:<t>`); set its
  // phase band so the render re-bands it this session. Pure attribute move —
  // display_number untouched, no relocation. Mirrors apply_v4_category_move_to_phase.
  if (kind === "category_move_to_phase") {
    if (!threadMatch) return false;
    const [, threadSlug] = threadMatch;
    const toPhase = typeof eventPayload.to_phase === "string"
      ? eventPayload.to_phase : "";
    if (!toPhase) return false;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      t.phase = toPhase;
      return true;
    }
    return false;
  }

  // P4d-3: category_move_to_subproject targets the Category (`thread:<t>`); set
  // its sub_project_slug so the render re-bands it under the new sub-project this
  // session. The display_number re-root is agent-side (needs the registry seq),
  // so numbers stay stale in-session until the next re-render/cron re-letters
  // them — the same "next cron re-projects it" contract this layer runs under.
  if (kind === "category_move_to_subproject") {
    if (!threadMatch) return false;
    const [, threadSlug] = threadMatch;
    const toSp = typeof eventPayload.to_sub_project_slug === "string"
      ? eventPayload.to_sub_project_slug : "";
    if (!toSp) return false;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      t.sub_project_slug = toSp;
      return true;
    }
    return false;
  }

  if (
    kind === "work_item_close" || kind === "work_item_reopen"
    || kind === "work_item_dismiss_closure_candidate"
  ) {
    if (!itemMatch) return false;
    // Locate the Item by its project-unique wi slug across ALL threads, not just
    // the one named in the targetId. wi slugs are project-unique (the invariant
    // action_merge relies on), so this is equivalent when the Item is where the
    // target says — AND it finds an Item that an optimistic item_move_to_category
    // just re-parented to another thread. B10 "Move & Close" dispatches this
    // work_item_close with the SOURCE targetId right after the move, so without
    // the cross-thread search the close would miss the just-moved Item and it
    // would render open under the target.
    const [, , wiSlug] = itemMatch;
    for (const t of payload.threads ?? []) {
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        if (kind === "work_item_close") {
          wi.status = "closed";
          // Option Y: closing clears the Item-level proposal (mirror the
          // absorber close_work_item NULLing closure_candidate_*).
          wi.closure_candidate = null;
        } else if (kind === "work_item_reopen") {
          wi.status = "open";
          // #38 policy 3 (binding amendment): a reopened Item whose group is
          // closed (or gone) returns UNGROUPED. The stamp's orphan sweep does
          // this every cron; mirror it here so the open Item never renders
          // under a closed group's header-only tombstone (open-work-hidden is
          // the worst failure class this system guards against).
          if (wi.subcategory_slug) {
            const g = (t.subcategories ?? []).find(
              (s) => s.slug === wi.subcategory_slug,
            );
            if (!g || g.status !== "open") wi.subcategory_slug = null;
          }
        } else {
          // work_item_dismiss_closure_candidate — park the Item open;
          // drop the proposal (mirror apply_v4_work_item_dismiss_*).
          wi.closure_candidate = null;
        }
        return true;
      }
    }
    return false;
  }

  if (kind === "action_open_question_dismiss") {
    // Drop the dismissed question from the rendered list so it disappears on
    // submit. Keyed on `qid` — the per-question ✎ always injects one, and it is
    // the identity that survives the synth re-wording the question between runs
    // (#52b); matching on text here would silently miss after any reword.
    if (!actionMatch) return false;
    const qid = eventPayload.qid;
    if (typeof qid !== "string" || !qid) return false;
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          const before = ai.open_questions_v4 ?? [];
          const after = before.filter((q) => q.qid !== qid);
          if (after.length === before.length) return false;
          ai.open_questions_v4 = after;
          return true;
        }
      }
    }
    return false;
  }

  if (kind === "action_open_question_resolve") {
    // A1 (Ray 2026-09-13): write the accepted/typed answer onto the matching
    // question so the answered render (briefings.ts:6523, keyed on `q.answer`)
    // survives the next re-render. Match by `qid` (the stable identity injected
    // by every per-question control); fall back to `question_text` for a legacy
    // question that has none. Both the one-click Accept (payload {qid, answer})
    // and the manual form (adds `answer`, may use `question_text`) route here.
    if (!actionMatch) return false;
    const qid = typeof eventPayload.qid === "string" ? eventPayload.qid : "";
    const qtext =
      typeof eventPayload.question_text === "string"
        ? eventPayload.question_text
        : "";
    if (!qid && !qtext) return false;
    const answer =
      typeof eventPayload.answer === "string" ? eventPayload.answer : "";
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          const q = (ai.open_questions_v4 ?? []).find((x) =>
            qid ? x.qid === qid : x.text === qtext,
          );
          if (!q) return false;
          // Prefer the submitted answer; keep any existing text if the resolve
          // carried none (still marks it answered so the ✓ renders).
          q.answer = answer || q.answer || "Answered";
          q.answered_at = todayISODate();
          return true;
        }
      }
    }
    return false;
  }

  if (kind === "action_set_report_tags") {
    // A2 (Ray 2026-09-13): echo the action's report gutter chip (KIL/558). The
    // event carries the FULL desired set (replace-whole — mirrors the absorber +
    // report_marks.ts), so assign it verbatim. The PSU chip echoes separately via
    // `kil-mark-changed`; report_tags had no optimistic path before this.
    if (!actionMatch) return false;
    if (!Array.isArray(eventPayload.report_tags)) return false;
    const tags = (eventPayload.report_tags as unknown[]).map((s) => String(s));
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          ai.report_tags = tags;
          return true;
        }
      }
    }
    return false;
  }

  if (kind === "item_set_report_tags") {
    // A2 item tier: echo the Item's report gutter chip. Replace-whole set.
    if (!itemMatch) return false;
    if (!Array.isArray(eventPayload.report_tags)) return false;
    const tags = (eventPayload.report_tags as unknown[]).map((s) => String(s));
    const [, threadSlug, wiSlug] = itemMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        wi.report_tags = tags;
        return true;
      }
    }
    return false;
  }

  if (kind === "action_set_associations") {
    // A1 (Ray 2026-09-15): echo the Action's association `.atag` chip. The event
    // carries the FULL desired set (replace-whole — mirrors the absorber +
    // action_set_report_tags above), so assign it verbatim. The Item's chip is a
    // pure rollup (itemAssociationUnion over its Actions), so writing the Action
    // node here also recomputes the Item chip on the next renderViewer — the item
    // pencil's cascade fans these per-Action, so one item "set all" echoes both
    // tiers. Coverage matches action_set_report_tags (threads); an axis project
    // renders its Actions under sub-project bands (threads[]).
    if (!actionMatch) return false;
    if (!Array.isArray(eventPayload.association_tags)) return false;
    const tags = (eventPayload.association_tags as unknown[]).map((s) => String(s));
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          ai.association_tags = tags;
          return true;
        }
      }
    }
    return false;
  }

  // E-7c (Ray 2026-09-15) + KIL-POINT (ultra #502 finding 3): the Item-tier
  // plain-string echoes share ONE applier keyed by field name — the event's payload
  // key IS the node field the render reads (narrative ← work_item_set_narrative,
  // feedback_absorb.py:615; point ← item_set_point, :697). Each writes the verbatim
  // string (blank clears) onto the Item node; threads-only coverage mirrors the
  // report_tags / associations appliers above. Was a no-echo the next renderViewer
  // wiped. A new Item-tier scalar kind adds one map row, not a copied branch.
  const ITEM_SCALAR_FIELD: Record<string, "narrative" | "point"> = {
    work_item_set_narrative: "narrative",
    item_set_point: "point",
  };
  const scalarField = ITEM_SCALAR_FIELD[kind];
  if (scalarField) {
    if (!itemMatch) return false;
    const raw = eventPayload[scalarField];
    const value = typeof raw === "string" ? raw : "";
    const [, threadSlug, wiSlug] = itemMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        wi[scalarField] = value;
        return true;
      }
    }
    return false;
  }

  if (kind === "action_set_narrative") {
    // E-7c (Ray 2026-09-15): the Action-tier sibling — echo an edited Action
    // narrative in-session. Verbatim text under `payload.narrative`.
    if (!actionMatch) return false;
    const text = typeof eventPayload.narrative === "string"
      ? eventPayload.narrative : "";
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          ai.narrative = text;
          return true;
        }
      }
    }
    return false;
  }

  if (kind === "work_item_rename") {
    // N1 (Ray 2026-09-22): echo an Item rename in-session. New display name under
    // `payload.to_name` (the absorber reads the same key, feedback_absorb.py:1099);
    // renderItemV4 reads `wi.name`. Mirrors the E-7c narrative appliers.
    if (!itemMatch) return false;
    const name = typeof eventPayload.to_name === "string"
      ? eventPayload.to_name.trim() : "";
    if (!name) return false;
    const [, threadSlug, wiSlug] = itemMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        wi.name = name;
        return true;
      }
    }
    return false;
  }

  if (kind === "action_rename") {
    // N1 sibling — echo an Action rename in-session. New title under
    // `payload.to_text` (absorber key, feedback_absorb.py:1152); render reads
    // `ai.text`.
    if (!actionMatch) return false;
    const text = typeof eventPayload.to_text === "string"
      ? eventPayload.to_text.trim() : "";
    if (!text) return false;
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          ai.text = text;
          return true;
        }
      }
    }
    return false;
  }

  if (
    kind === "action_close" || kind === "action_reopen"
    || kind === "action_dismiss_closure_candidate"
    || kind === "action_priority_change"
  ) {
    if (!actionMatch) return false;
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          if (kind === "action_close") {
            ai.status = "closed";
            // Absorber clears closure_candidate on close
            // (feedback_absorb.py): a proposed-then-closed Action
            // shouldn't keep the "(Agent proposes closing)" suffix.
            ai.closure_candidate = null;
          } else if (kind === "action_reopen") {
            ai.status = "open";
          } else if (kind === "action_dismiss_closure_candidate") {
            ai.closure_candidate = null;
          } else {
            // action_priority_change — mirror feedback_absorb.py:333.
            // Form key is `new_priority` (NOT priority_status). Clear
            // reason/impact/off_hold to null when absent (Active/On-Hold
            // hide them) so no stale metadata clings to the new pill.
            // `due` is intentionally NOT touched — the absorber drops it
            // for a priority change, so showing it would revert next cron.
            const np = eventPayload.new_priority;
            if (typeof np === "string") ai.priority_status = np;
            ai.reason =
              typeof eventPayload.reason === "string" ? eventPayload.reason : null;
            ai.impact =
              typeof eventPayload.impact === "string" ? eventPayload.impact : null;
            ai.off_hold_condition =
              typeof eventPayload.off_hold_condition === "string"
                ? eventPayload.off_hold_condition
                : null;
          }
          return true;
        }
      }
    }
    return false;
  }

  // C5 (Ray 2026-10-18, was 2026-07-21 gray): work_item_acknowledge /
  // work_item_not_relevant REMOVE the Item in-session instead of graying it.
  // Both move the entity to a terminal status the cron DROPS from the briefing
  // (enforce_state_acknowledged / _not_relevant, agent/action_items.py:198,226),
  // so the eventual state is "gone". The old echo set status='acknowledged' → a
  // grey "Acknowledged · not tracked" pill (briefings.ts:6186) that LINGERED until
  // the next cron (Ray's B12.1.10 "why is this staying active and tagged?"). Removal
  // matches the drop AND still relaxes the parent Category's close gate (a removed
  // Item isn't an open Item). Idempotent on replay — once the cron drops it, the
  // splice is a no-op (returns false). not_relevant rides along: identical cron-drop,
  // identical lag — graying one and removing the other would be an arbitrary split.
  if (kind === "work_item_acknowledge" || kind === "work_item_not_relevant") {
    if (!itemMatch) return false;
    const [, threadSlug, wiSlug] = itemMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      const before = (t.work_items ?? []).length;
      t.work_items = (t.work_items ?? []).filter((wi) => wi.slug !== wiSlug);
      return t.work_items.length !== before;
    }
    return false;
  }

  // C5 (Ray 2026-10-18): action_acknowledge / action_not_relevant REMOVE the Action
  // in-session (see the Item branch above). Removal keeps the parent Item's close
  // gate honest — a removed child is not an open child, so "Close Item" un-grays
  // once every child is resolved (openActionCount counts status==='open' survivors,
  // briefings.ts:6362). Mirrors the cron's drop of the acknowledged/not-relevant row.
  if (kind === "action_acknowledge" || kind === "action_not_relevant") {
    if (!actionMatch) return false;
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        const before = (wi.action_items ?? []).length;
        wi.action_items = (wi.action_items ?? []).filter((ai) => ai.ai_slug !== aiSlug);
        return wi.action_items.length !== before;
      }
    }
    return false;
  }

  // C5 (Ray 2026-10-18): informational_acknowledge drops the Needs-Input row from the
  // active queue immediately — it had NO optimistic path, so an acknowledged
  // informational/unfiled entry LINGERED in the active list until the cron (same
  // echo-gap family). The target carries the id (`informational:<id>` or, since #60
  // widened the kind, `unfiled:<id>`); removeNeedsInputEntry clears it from whichever
  // queue holds it. The cron then files it under the read-only "Acknowledged this
  // period" collapse; this replay is idempotent once that lands.
  if (kind === "informational_acknowledge") {
    const m = targetId.match(/^(?:informational|unfiled):(.+)$/);
    if (!m) return false;
    return removeNeedsInputEntry(payload, m[1]!);
  }

  // --- #38 sub-category grouping --------------------------------------------
  // subcategory_add targets the parent Category (`thread:<t>`): append the
  // group (or idempotently refresh its name if the slug already exists, the
  // absorber's already-exists branch) so pickers + the render partition see it
  // this session. Mirrors apply_v4_subcategory_add.
  if (kind === "subcategory_add") {
    if (!threadMatch) return false;
    const [, threadSlug] = threadMatch;
    const slug = typeof eventPayload.subcategory_slug === "string"
      ? eventPayload.subcategory_slug : "";
    const name = typeof eventPayload.name === "string" ? eventPayload.name : "";
    if (!slug) return false;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      const groups = (t.subcategories ??= []);
      const existing = groups.find((g) => g.slug === slug);
      if (existing) {
        if (name) existing.name = name;
      } else {
        const maxSeq = groups.reduce(
          (m, g) => Math.max(m, typeof g.seq === "number" ? g.seq : 0),
          0,
        );
        groups.push({
          slug, name, status: "open",
          created_at: todayISODate(), seq: maxSeq + 1,
        });
      }
      return true;
    }
    return false;
  }

  // item_move_to_subcategory targets the Item (`thread:<t>/work_item:<wi>`);
  // set its grouping pointer. `to_subcategory_slug` null/empty = ungroup.
  if (kind === "item_move_to_subcategory") {
    if (!itemMatch) return false;
    const [, threadSlug, wiSlug] = itemMatch;
    const to = eventPayload.to_subcategory_slug;
    const ptr = typeof to === "string" && to ? to : null;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        wi.subcategory_slug = ptr;
        return true;
      }
    }
    return false;
  }

  // subcategory_rename / _close / _reopen target the group
  // (`thread:<t>/subcategory:<sc>`); mutate the matching subcategories[] entry.
  if (
    kind === "subcategory_rename" || kind === "subcategory_close"
    || kind === "subcategory_reopen"
  ) {
    if (!subcategoryMatch) return false;
    const [, threadSlug, scSlug] = subcategoryMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const g of t.subcategories ?? []) {
        if (g.slug !== scSlug) continue;
        if (kind === "subcategory_rename") {
          const to = eventPayload.to_name;
          if (typeof to === "string" && to.trim()) g.name = to.trim();
        } else if (kind === "subcategory_close") {
          g.status = "closed";
          g.closed_at = todayISODate();
          g.closed_reason = typeof eventPayload.reason === "string"
            ? eventPayload.reason : null;
        } else {
          g.status = "open";
          g.closed_at = null;
          g.closed_reason = null;
        }
        return true;
      }
    }
    return false;
  }

  // #74b: subcategory_delete targets the group (`thread:<t>/subcategory:<sc>`).
  // UNLIKE close (which tombstones the row), delete REMOVES the group from
  // subcategories[] AND ungroups its member Items (subcategory_slug → null, any
  // status) so the render partition shows them flat. Mirrors
  // apply_v4_subcategory_delete (drop row + null members).
  if (kind === "subcategory_delete") {
    if (!subcategoryMatch) return false;
    const [, threadSlug, scSlug] = subcategoryMatch;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      const groups = t.subcategories;
      if (!groups) return false;
      const idx = groups.findIndex((g) => g.slug === scSlug);
      if (idx === -1) return false;
      groups.splice(idx, 1);
      for (const wi of t.work_items ?? []) {
        if (wi.subcategory_slug === scSlug) wi.subcategory_slug = null;
      }
      return true;
    }
    return false;
  }

  // #76 + OPT-1a: action_to_item echoes BOTH sides in-session. (1) CLOSE the
  // source Action in place with the "Promoted to Item: X" breadcrumb (it moves to
  // Recently Closed under its old Item), and (2) materialize the newly-created
  // Item so the promote's target renders this session. Mirrors
  // apply_v4_action_to_item (agent/state.py:2261): the new Item is created EMPTY
  // under `to_thread_slug` (name + the source Action's inherited narrative); the
  // Action is NOT re-parented — it stays closed under its source Item, exactly as
  // the cron settles it (re-parenting would make the echo diverge from the
  // next-render state). display_number is stale in-session (the agent numbers it
  // next render — same contract as the moves). Idempotent under
  // rebuildOptimisticLayer: the stub is skipped when an Item with
  // new_work_item_slug already exists under the target (a prior replay's stub OR
  // the real cron-created node); if to_thread_slug's Category isn't in this
  // payload (brand-new Category, rare) the close alone still echoes and the Item
  // surfaces next render — never create a dangling thread.
  if (kind === "action_to_item") {
    if (!actionMatch) return false;
    const [, threadSlug, wiSlug, aiSlug] = actionMatch;
    const label = typeof eventPayload.name === "string" ? eventPayload.name : "";
    const newSlug = typeof eventPayload.new_work_item_slug === "string"
      ? eventPayload.new_work_item_slug : "";
    const toThread = typeof eventPayload.to_thread_slug === "string"
      ? eventPayload.to_thread_slug : "";
    const newSubcat = typeof eventPayload.new_item_subcategory_slug === "string"
      && eventPayload.new_item_subcategory_slug
      ? eventPayload.new_item_subcategory_slug : null;
    let changed = false;
    // (1) close the source Action, capturing its narrative to seed the new Item
    // (mirror apply_v4_item_add's narrative= inheritance).
    let inheritedNarrative: string | null = null;
    for (const t of payload.threads ?? []) {
      if (t.slug !== threadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== wiSlug) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug !== aiSlug) continue;
          inheritedNarrative = typeof ai.narrative === "string" ? ai.narrative : null;
          ai.status = "closed";
          ai.closed_reason = label ? `Promoted to Item: ${label}` : "Promoted to Item";
          ai.closure_candidate = null;
          changed = true;
        }
      }
    }
    // (2) materialize the created Item under to_thread_slug (idempotent + never
    // dangling — see the block comment).
    if (newSlug && toThread) {
      const targetThread = (payload.threads ?? []).find((t) => t.slug === toThread);
      if (targetThread) {
        targetThread.work_items = targetThread.work_items ?? [];
        const exists = targetThread.work_items.some((wi) => wi.slug === newSlug);
        if (!exists) {
          targetThread.work_items.push({
            slug: newSlug,
            name: label || newSlug,
            narrative: inheritedNarrative,
            status: "open",
            subcategory_slug: newSubcat,
            action_items: [],
          });
          changed = true;
        }
      }
    }
    return changed;
  }

  // P-5 fan-out ②: item_move_to_category — re-parent the source Item (targetId)
  // to the target Category (`to_thread_slug`). Mirror of
  // apply_v4_item_move_to_category (agent/state.py): splice the Item node OUT of
  // the source Category's work_items and append it to the target's, NULLing its
  // subcategory_slug (the absorber's eager exit of the old group). The Item's
  // node carries its own status/narrative/actions/report_tags, so a plain move
  // preserves them. display_number is stale in-session (the agent re-numbers
  // under the target); the next re-render/cron settles it — same contract as
  // category_move_to_subproject. Guards mirror the absorber: target Category must
  // exist (else no-op — never splice to nowhere), from==to is a no-op, and if the
  // Item already sits under the target (a replayed/duplicate event) just drop the
  // source occurrence. wi slugs are project-unique, so the search is unambiguous.
  if (kind === "item_move_to_category") {
    if (!itemMatch) return false;
    const [, srcThreadSlug, wiSlug] = itemMatch;
    const toThread = typeof eventPayload.to_thread_slug === "string"
      ? eventPayload.to_thread_slug : "";
    if (!toThread || toThread === srcThreadSlug) return false;
    const targetThread = (payload.threads ?? []).find(
      (t) => t.slug === toThread,
    );
    if (!targetThread) return false;
    targetThread.work_items = targetThread.work_items ?? [];
    const alreadyUnderTarget = targetThread.work_items.some(
      (wi) => wi.slug === wiSlug,
    );
    for (const t of payload.threads ?? []) {
      if (t.slug !== srcThreadSlug) continue;
      const list = t.work_items ?? [];
      const idx = list.findIndex((wi) => wi.slug === wiSlug);
      if (idx === -1) return false;  // nothing to move
      const [node] = list.splice(idx, 1);
      if (!alreadyUnderTarget && node) {
        node.subcategory_slug = null;  // exit the old group (mirror the absorber)
        targetThread.work_items.push(node);
      }
      return true;
    }
    return false;
  }

  // P-5: action_move_to_item — re-parent the source Action (targetId) to the
  // target Item (`to_work_item_slug`). Mirror of apply_v4_action_move_to_item
  // (agent/state.py): splice the node OUT of the source Item's action_items and
  // append it to the target's — the node carries its own status/priority/
  // narrative/questions, so a plain move preserves them. The display_number is
  // stale in-session (the agent re-assigns a fresh one under the target); the
  // next re-render/cron re-numbers it, same contract as category_move_to_*.
  // Guards mirror the absorber: target Item must exist (else no-op — never
  // splice to nowhere), from==to is a no-op, and if the Action is already under
  // the target (a replayed/duplicate event) just drop the source occurrence.
  // wi slugs are project-unique, so the cross-thread search by slug is safe.
  if (kind === "action_move_to_item") {
    if (!actionMatch) return false;
    const [, srcThreadSlug, srcWiSlug, aiSlug] = actionMatch;
    const toWi = typeof eventPayload.to_work_item_slug === "string"
      ? eventPayload.to_work_item_slug : "";
    if (!toWi || toWi === srcWiSlug) return false;
    // Locate the target Item (any thread — wi slugs are project-unique).
    let targetWi: NonNullable<
      NonNullable<OptimisticPayload["threads"]>[number]["work_items"]
    >[number] | undefined;
    for (const t of payload.threads ?? []) {
      for (const wi of t.work_items ?? []) {
        if (wi.slug === toWi) { targetWi = wi; break; }
      }
      if (targetWi) break;
    }
    if (!targetWi) return false;
    targetWi.action_items = targetWi.action_items ?? [];
    // If the Action already sits under the target, this is a stale re-emit —
    // just drop it from the source (idempotent, mirrors the absorber).
    const alreadyUnderTarget = targetWi.action_items.some(
      (a) => a.ai_slug === aiSlug,
    );
    // Find + remove the source node.
    for (const t of payload.threads ?? []) {
      if (t.slug !== srcThreadSlug) continue;
      for (const wi of t.work_items ?? []) {
        if (wi.slug !== srcWiSlug) continue;
        const list = wi.action_items ?? [];
        const idx = list.findIndex((a) => a.ai_slug === aiSlug);
        if (idx === -1) return false;  // nothing to move
        const [node] = list.splice(idx, 1);
        if (!alreadyUnderTarget && node) targetWi.action_items.push(node);
        return true;
      }
    }
    return false;
  }

  // Reorg-toast fan-out: thread_merge — merge the source Category (targetId)
  // into the target (`target_slug`). Mirror of apply_thread_merge's
  // user-visible effect: move every source-Category Item to the target
  // (nulling subcategory_slug — the agent re-migrates groups on absorb) and
  // close the now-empty source Category so it grays into "merged". The SPA
  // always sends a single source (the Category the menu is open on). Guards:
  // target + source must exist, source != target. Retract-safe + idempotent —
  // rebuildOptimisticLayer replays from the pristine baseline, and a re-apply
  // finds the source already emptied.
  if (kind === "thread_merge") {
    if (!threadMatch) return false;
    const [, srcThreadSlug] = threadMatch;
    const toThread = typeof eventPayload.target_slug === "string"
      ? eventPayload.target_slug : "";
    if (!toThread || toThread === srcThreadSlug) return false;
    const target = (payload.threads ?? []).find((t) => t.slug === toThread);
    const source = (payload.threads ?? []).find((t) => t.slug === srcThreadSlug);
    if (!target || !source) return false;
    target.work_items = target.work_items ?? [];
    for (const wi of source.work_items ?? []) {
      wi.subcategory_slug = null;
      target.work_items.push(wi);
    }
    source.work_items = [];
    source.status = "closed";
    return true;
  }

  // A①: work_item_merge — the picked source Items fold into the target and
  // CLOSE (feedback_absorb.py + state.apply_work_item_merge), but their
  // context carries forward into the target, so they're "merged", not just
  // "closed". Mark status=closed + a merge marker so they route to the
  // "Recently Merged → into X" section. `target_label` rides the event
  // (threaded by forms.ts) so the label survives reload + names the target.
  if (kind === "work_item_merge") {
    const sources = toSlugList(eventPayload.source_work_item_slugs);
    if (!sources.length) return false;
    const label = typeof eventPayload.target_label === "string"
      ? eventPayload.target_label : "";
    let changed = false;
    for (const t of payload.threads ?? []) {
      for (const wi of t.work_items ?? []) {
        if (wi.slug && sources.includes(wi.slug)) {
          wi.status = "closed";
          wi.closed_reason = label ? `merged into ${label}` : "merged";
          changed = true;
        }
      }
    }
    return changed;
  }

  // A①: action_merge — the picked source Actions close into the target; same
  // "merged" treatment. B8: the picker now spans Items, so a source may live
  // under its ORIGIN Item until next run's re-parent applies — gray it there
  // too. `merge_origin_work_item_slugs` (threaded by forms.ts) scopes the
  // search to the target Item + the picked sources' origins, so a coincidental
  // same-ai_slug under an unrelated Item is never touched. wi slugs are
  // project-unique, so searching across threads by slug is safe.
  if (kind === "action_merge") {
    if (!actionMatch) return false;
    const [, , wiSlug] = actionMatch;
    const sources = toSlugList(eventPayload.source_action_item_slugs);
    if (!sources.length) return false;
    const label = typeof eventPayload.target_label === "string"
      ? eventPayload.target_label : "";
    const searchWis = new Set<string>([
      wiSlug ?? "",
      ...toSlugList(eventPayload.merge_origin_work_item_slugs),
    ]);
    let changed = false;
    for (const t of payload.threads ?? []) {
      for (const wi of t.work_items ?? []) {
        if (!wi.slug || !searchWis.has(wi.slug)) continue;
        for (const ai of wi.action_items ?? []) {
          if (ai.ai_slug && sources.includes(ai.ai_slug)) {
            ai.status = "closed";
            ai.closed_reason = label ? `merged into ${label}` : "merged";
            changed = true;
          }
        }
      }
    }
    return changed;
  }
  return false;
}
